import { useCallback, useMemo, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, FlexProps, Stack, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { User } from "features/User/models";
import { camelCase, pick } from "lodash";
import { TextTransformType } from "components/Form/typings";
import { useNewHireJourneyDetailQuery, useUpdateNewHireJourney } from "features/NewHireJourney/hooks";
import { NewHireJourneyId } from "models/newHire";
import DynamicUserField from "features/User/DynamicForm/DynamicUserField";
import { UserUpdateFormValues, userUpdateSchema, values } from "features/User/DynamicForm/formDefinition";

export const FIRST_NAME_FIELD = {
  label: "First Name",
  name: "firstName",
};
export const LAST_NAME_FIELD = {
  label: "Last Name",
  name: "lastName",
};
export const TITLE_FIELD = {
  label: "Job Title",
  name: "title",
};
export const START_DATE_FIELD = {
  label: "Start Date",
  name: "startDate",
  type: "date",
  textTransform: "lowercase",
};
export const PERSONAL_EMAIL_FIELD = {
  label: "Personal Email",
  name: "personalEmail",
  type: "email",
  textTransform: "lowercase",
};
export const WORK_EMAIL_FIELD = {
  label: "Future Work Email",
  name: "workEmail",
  type: "email",
  textTransform: "lowercase" as TextTransformType,
};
export const LOCATION_FIELD = {
  label: "Location",
  name: "location",
  helperText: "Location of where the new hire will be during onboarding",
};
export const LINKEDIN_URL_FIELD = {
  label: "Linkedin Url",
  name: "linkedinUrl",
  helperText: "The url for the person's Linkedin profile",
};
export const ONBOARDING_PLAN_URL_FIELD = {
  label: "Onboarding Plan Url",
  name: "onboardingPlanUrl",
  helperText: "The url for the onboarding plan",
};
export const TIMEZONE_FIELD = {
  label: "Timezone",
  name: "timezone",
  helperText: "Timezone of where the new hire will be during onboarding",
};

export const MANAGER_FIELD = {
  label: "Manager",
  name: "managerId",
};
export const BUDDY_FIELD = {
  label: "Buddy",
  name: "buddyId",
};
export const PEOPLE_TEAM_CONTACT_FIELD = {
  label: "People Team Contact",
  name: "peopleTeamContactId",
};

export type { UserUpdateFormValues };

interface UserUpdateFormProps extends FlexProps {
  newHireJourneyId: NewHireJourneyId;
  fields: Array<keyof UserUpdateFormValues>;
  onSuccess?: () => void;
  onClose?: () => void;
}

export default function UserUpdateForm({
  newHireJourneyId,
  fields,
  onSuccess = () => {},
  onClose = () => {},
  ...restProps
}: UserUpdateFormProps) {
  const camelFields = useMemo(() => fields.map((field) => camelCase(field) as keyof UserUpdateFormValues), [fields]);
  const toast = useToast({ status: "success" });

  const { data: newHireJourney, isLoading } = useNewHireJourneyDetailQuery(newHireJourneyId || "");

  const userWithJourney = useMemo(() => {
    if (!newHireJourney?.newHire) return undefined;
    return { ...newHireJourney.newHire, newHireJourney } as User;
  }, [newHireJourney]);

  const defaultValues = useMemo(() => pick(values(userWithJourney), camelFields), [userWithJourney, camelFields]);

  const form = useForm({
    mode: "onBlur",
    resolver: yupResolver(userUpdateSchema),
    values: defaultValues,
  });

  const {
    formState: { dirtyFields },
    getValues,
    reset,
  } = form;

  const { mutate: onSubmit } = useUpdateNewHireJourney(newHireJourneyId);
  const lastSavedRef = useRef<Record<string, unknown>>({});

  const saveOnBlur = useCallback(() => {
    const currentValues = getValues();
    const dirtyKeys = Object.keys(dirtyFields);
    if (dirtyKeys.length === 0) return;

    const changedData: Record<string, unknown> = {};
    dirtyKeys.forEach((key) => {
      const val = currentValues[key];
      if (lastSavedRef.current[key] !== val) {
        changedData[key] = val;
      }
    });

    if (Object.keys(changedData).length === 0) return;

    onSubmit(changedData, {
      onSuccess: () => {
        Object.assign(lastSavedRef.current, changedData);
        reset(currentValues, { keepValues: true });
        toast({ title: "Saved", status: "success", duration: 1500 });
      },
    });
  }, [dirtyFields, getValues, onSubmit, reset, toast]);

  return (
    <Flex direction="column" {...restProps}>
      <Stack width="100%">
        {camelFields.map((fieldName) => (
          <DynamicUserField
            key={fieldName}
            fieldName={fieldName}
            form={form}
            size="sm"
            disabled={isLoading}
            onBlur={saveOnBlur}
          />
        ))}
      </Stack>
    </Flex>
  );
}
